package org.braunequip.web.pages.braunadmin

import androidx.compose.runtime.*
import com.varabyte.kobweb.browser.dom.ElementTarget
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.icons.fa.*
import com.varabyte.kobweb.silk.components.layout.HorizontalDivider
import com.varabyte.kobweb.silk.components.overlay.Tooltip
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import kotlinx.browser.document
import kotlinx.coroutines.launch
import org.braunequip.web.components.forms.Assets.AdminAssetForm
import org.braunequip.web.components.forms.Maintenance.AddMaintenanceScheduleForm
import org.braunequip.web.components.forms.Maintenance.ConfirmMaintItemModal
import org.braunequip.web.components.forms.Maintenance.MaintItemDetails
import org.braunequip.web.components.layouts.AdminPageLayout
import org.braunequip.web.components.models.AssetDTO
import org.braunequip.web.components.models.MaintItemsDTO
import org.braunequip.web.components.models.MaintSchedulesDTO
import org.braunequip.web.components.sections.Maintenance.AssetMaintenanceItemsTable
import org.braunequip.web.components.widgets.*
import org.braunequip.web.styles.AdminStyles.AdminDescriptionStyle
import org.braunequip.web.styles.AdminStyles.AdminDetailsColumnStyle
import org.braunequip.web.styles.AdminStyles.AdminSubtitleHeaderStyle
import org.braunequip.web.styles.AdminStyles.AdminTitleHeaderStyle
import org.braunequip.web.util.*
import org.braunequip.web.util.Constants.FONT_FAMILY
import org.braunequip.web.util.Constants.FONT_FAMILY2
import org.braunequip.web.util.Constants.SHARED.ROWS_PER_REQ
import org.braunequip.web.util.Routes.Admin.FRONT_ASSET_ROUTE
import org.jetbrains.compose.web.attributes.selected
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLSelectElement

data class AdminAssetsDetailUiState(
    var isLoading: Boolean = true,
    var errorMessage: String? = null,

    var eroadPopup: Boolean = false,
    var updatePopup: Boolean = false,
    var deletePopup: Boolean = false,
    var schedulePopup: Boolean = false,

    var selectedMaintItem: MaintItemsDTO? = null,
    var readMaintPopup: Boolean = false,
    var deleteMaintPopup: Boolean = false,
    var markCompletePopup: Boolean = false

)

@Page("assets/{id}")
@Composable
fun AdminAssetDetailsPage() {
    isUserLoggedIn {
        AdminPageLayout {
            AdminAssetDetailsScreen()
        }
    }
}

@Composable
fun AdminAssetDetailsScreen() {
    val ctx = rememberPageContext()
    val breakpoint = rememberBreakpoint()
    val scope = rememberCoroutineScope()
    val postId = ctx.route.params["id"]

    val alertManager = remember { AlertManager() }
    alertManager.DisplayAlerts()

    var assetDetails by remember { mutableStateOf<AssetDTO?>(null) }
    var uiState by remember { mutableStateOf(AdminAssetsDetailUiState()) }

    //For Show More button
    val assetMaintItems = remember { mutableStateListOf<MaintItemsDTO>() }
    var itemsRowsToSkip by remember { mutableStateOf(0) }
    var itemsShowMoreVisibility by remember { mutableStateOf(false) }

    var itemsLoading by remember { mutableStateOf(true) }

    //Default = null (for showing Pending/Overdue), Pending, Completed...
    //See Constants.kt, MaintItemStatus
    var fetchItemStatus by remember { mutableStateOf<Short?>(null) }

    // Asset
    if (
        uiState.eroadPopup
    ) {
        ConfirmModal(
            actionText = "pull the latest ERoad information",
            subText = "This will overwrite current Asset miles with latest from ERoad, and update ERoad ID if necessary." ,
            onConfirm = {
                scope.launch {
                    updateFetchAssetWithERoadData(
                        id = postId!!,
                        onSuccess = {
                            assetDetails = it

                            itemsRowsToSkip = 0 //Reset
                            scope.launch {
                                fetchAssetMaintenanceItemsByID(
                                    assetID = postId,
                                    status = fetchItemStatus,
                                    skip = itemsRowsToSkip,
                                    onSuccess = {
                                        itemsLoading = false
                                        assetMaintItems.clear() //Reset
                                        assetMaintItems.addAll(it)
                                        itemsRowsToSkip += ROWS_PER_REQ
                                        itemsShowMoreVisibility = it.size >= ROWS_PER_REQ
                                    },
                                    onError = {
                                        console.log(it)
                                    }
                                )
                            }
                            uiState = uiState.copy(
                                isLoading = false
                            )
                        },
                        onError = {
                            uiState = uiState.copy(
                                isLoading = false
                            )

                            alertManager.addAlert(
                                UserAlert(
                                    message = it,
                                    color = Colors.Red,
                                    borderColor = Colors.DarkRed
                                )
                            )
                        }
                    )
                }
                uiState = uiState.copy(
                    eroadPopup = false,
                )
            },
            onDismiss = {
                uiState = uiState.copy(
                    eroadPopup = false,
                )
            }
        )
    }

    if (
        uiState.updatePopup
    ) {
        AdminAssetForm(
            toUpdateAsset = assetDetails,
            onSubmit = {
                scope.launch {
                    updateAsset(it)
                    uiState = uiState.copy(
                        updatePopup = false,
                    )

                    fetchAssetById(
                        id = postId!!,
                        onSuccess = {
                            assetDetails = it
                            uiState = uiState.copy(
                                isLoading = false
                            )
                        },
                        onError = {
                            uiState = uiState.copy(
                                errorMessage = it,
                                isLoading = false
                            )
                        }
                    )

                    itemsRowsToSkip = 0
                    fetchAssetMaintenanceItemsByID(
                        assetID = postId!!,
                        status = fetchItemStatus,
                        skip = itemsRowsToSkip,
                        onSuccess = {
                            itemsLoading = false
                            assetMaintItems.clear()
                            assetMaintItems.addAll(it)
                            itemsRowsToSkip += ROWS_PER_REQ
                            itemsShowMoreVisibility = it.size >= ROWS_PER_REQ
                        },
                        onError = {
                            console.log(it)
                        }
                    )

                    alertManager.addAlert(
                        UserAlert(
                            message = "Asset Updated!",
                            color = Colors.Green,
                            borderColor = Colors.DarkGreen
                        )
                    )
                }
            },
            onDismiss = {
                uiState = uiState.copy(updatePopup = false)
            }
        )
    }

    if (
        uiState.deletePopup
    ) {
        ConfirmModal(
            actionText = "Delete Asset",
            onConfirm = {
                scope.launch {
                    val result = deleteSelectedAssets(ids = listOf("${assetDetails?.id}"))
                    if (result) {
                        ctx.router.tryRoutingTo(FRONT_ASSET_ROUTE)
                    }
                }
            },
            onDismiss = {
                uiState = uiState.copy(
                    deletePopup = false,
                )
            }
        )
    }

    // Maintenance Items
    if (
        uiState.readMaintPopup
    ) {
        if (uiState.selectedMaintItem != null) {
            MaintItemDetails(
                item = uiState.selectedMaintItem!!,
                onConfirm = {

                },
                onDismiss = {
                    uiState = uiState.copy(
                        readMaintPopup = false,
                    )
                }
            )
        }
    }

    if (
        uiState.deleteMaintPopup
    ) {
        ConfirmModal(
            actionText = "Delete Maintenance Item",
            subText = uiState.selectedMaintItem?.completedDate?.let { "Delete Completed Maintenance Item?" } ?: "Deleting will remove this applied schedule!" ,
            onConfirm = {
                scope.launch {
                    if (uiState.selectedMaintItem?.id != null) {
                        val result = deleteSelectedMaintenanceItems(id = uiState.selectedMaintItem!!.id!!)
                        if (result) {
                            itemsRowsToSkip = 0
                            fetchAssetMaintenanceItemsByID(
                                assetID = postId!!,
                                status = fetchItemStatus,
                                skip = itemsRowsToSkip,
                                onSuccess = {
                                    itemsLoading = false
                                    assetMaintItems.clear()
                                    assetMaintItems.addAll(it)
                                    itemsRowsToSkip += ROWS_PER_REQ
                                    itemsShowMoreVisibility = it.size >= ROWS_PER_REQ
                                },
                                onError = {
                                    console.log(it)
                                }
                            )

                            uiState = uiState.copy(
                                isLoading = false
                            )

                            alertManager.addAlert(
                                UserAlert(
                                    message = "Deleted Maintenance Item!",
                                    color = Colors.Green,
                                    borderColor = Colors.DarkGreen
                                )
                            )
                        }
                    }
                }
                uiState = uiState.copy(
                    deleteMaintPopup = false,
                )
            },
            onDismiss = {
                uiState = uiState.copy(
                    deleteMaintPopup = false,
                )
            }
        )
    }

    if (
        uiState.markCompletePopup
    ) {
        if (uiState.selectedMaintItem != null) {
            ConfirmMaintItemModal(
                asset = assetDetails!!,
                item = uiState.selectedMaintItem!!,
                onConfirm = { completeRequest ->
                    scope.launch {
                        val result = updateCompleteMaintenanceItem(
                            completedItem = completeRequest
                        )

                        fetchAssetById(
                            id = postId!!,
                            onSuccess = {
                                assetDetails = it
                                uiState = uiState.copy(
                                    isLoading = false
                                )
                            },
                            onError = {
                                uiState = uiState.copy(
                                    errorMessage = it,
                                    isLoading = false
                                )
                            }
                        )

                        itemsRowsToSkip = 0
                        fetchAssetMaintenanceItemsByID(
                            assetID = postId!!,
                            status = fetchItemStatus,
                            skip = itemsRowsToSkip,
                            onSuccess = {
                                itemsLoading = false
                                assetMaintItems.clear()
                                assetMaintItems.addAll(it)
                                itemsRowsToSkip += ROWS_PER_REQ
                                itemsShowMoreVisibility = it.size >= ROWS_PER_REQ
                            },
                            onError = {
                                console.log(it)
                            }
                        )
                    }

                    alertManager.addAlert(
                        UserAlert(
                            message = "Completed Item!",
                            color = Colors.Green,
                            borderColor = Colors.DarkGreen
                        )
                    )

                    uiState = uiState.copy(
                        markCompletePopup = false,
                    )
                },
                onDismiss = {
                    uiState = uiState.copy(
                        markCompletePopup = false,
                    )
                }
        )
        }
    }

    // Maintenance Schedule
    if (
        uiState.schedulePopup
    ) {
        if (assetDetails != null) {
            AddMaintenanceScheduleForm(
                assetIDList = listOf(
                    Pair(assetDetails!!.id!!, assetDetails!!.assetname)
                ),
                onSubmit = { newMaintItemRequest ->
                    scope.launch {
                        uiState = uiState.copy(
                            isLoading = true
                        )

                        submitScheduleForItems(
                            request = newMaintItemRequest,
                            onFinish = { text, MaintResponse ->
                                uiState = uiState.copy(
                                    schedulePopup = false
                                )
                                val skippedAssets = MaintResponse
                                alertManager.addAlert(
                                    UserAlert(
                                        message = "Applied Schedule!",
                                        color = Colors.Green,
                                        borderColor = Colors.DarkGreen
                                    )
                                )
                                if (skippedAssets.skippedAssets.isNotEmpty()) {
                                    alertManager.addAlert(
                                        UserAlert(
                                            message = "Schedule Already applied on Asset: ${skippedAssets.skippedAssets.joinToString(", ") { it.id.toString() }}",
                                            color = Colors.Red,
                                            borderColor = Colors.DarkRed,
                                            durationMillis = 10000
                                        )
                                    )
                                }
                            }
                        )

                        if (postId != null) {
                            fetchAssetById(
                                id = postId,
                                onSuccess = {
                                    assetDetails = it
                                    uiState = uiState.copy(
                                        isLoading = false
                                    )
                                },
                                onError = {
                                    uiState = uiState.copy(
                                        errorMessage = it,
                                        isLoading = false
                                    )
                                }
                            )

                            itemsRowsToSkip = 0
                            fetchAssetMaintenanceItemsByID(
                                assetID = postId,
                                status = fetchItemStatus,
                                skip = itemsRowsToSkip,
                                onSuccess = {
                                    itemsLoading = false
                                    assetMaintItems.clear()
                                    assetMaintItems.addAll(it)
                                    itemsRowsToSkip += ROWS_PER_REQ
                                    itemsShowMoreVisibility = it.size >= ROWS_PER_REQ
                                },
                                onError = {
                                    console.log(it)
                                }
                            )
                        }

                        uiState = uiState.copy(
                            isLoading = false
                        )
                    }
                },
                onDismiss = {
                    uiState = uiState.copy(
                        schedulePopup = false
                    )
                }
            )
        }
    }

    //Initial Asset Load
    LaunchedEffect(postId) {
        if (postId.isNullOrBlank()) {
            uiState = uiState.copy(
                errorMessage = "No asset ID provided in URL.",
                isLoading = false
            )
            return@LaunchedEffect
        }

        fetchAssetById(
            id = postId,
            onSuccess = {
                assetDetails = it
                uiState = uiState.copy(
                    isLoading = false
                )
            },
            onError = {
                uiState = uiState.copy(
                    errorMessage = it,
                    isLoading = false
                )
            }
        )
    }

    // Fetch New Maintenance Items off status
    LaunchedEffect(fetchItemStatus) {

        if (postId != null) {
            itemsRowsToSkip = 0 //Reset
            fetchAssetMaintenanceItemsByID(
                assetID = postId,
                status = fetchItemStatus,
                skip = itemsRowsToSkip,
                onSuccess = {
                    itemsLoading = false
                    assetMaintItems.clear() //Reset
                    assetMaintItems.addAll(it)
                    itemsRowsToSkip += ROWS_PER_REQ
                    itemsShowMoreVisibility = it.size >= ROWS_PER_REQ
                },
                onError = {
                    console.log(it)
                }
            )
        }
    }

    Section(
        attrs = Modifier
            .padding(16.px)
            .fillMaxSize()
            .display(DisplayStyle.Flex)
            .flexDirection(FlexDirection.Column)
            .toAttrs()
    ) {
        when {
            uiState.isLoading -> {
                Box(
                    modifier = Modifier
                        .fillMaxSize(),
                    contentAlignment = Alignment.Center
                ) {
                    loadingSpinner()
                }
            }
            uiState.errorMessage != null -> {
                Box(
                    modifier = Modifier
                        .fillMaxSize(),
                    contentAlignment = Alignment.Center
                ) {
                    Column(
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        H2(
                            attrs = AdminTitleHeaderStyle.toAttrs()
                        ) {
                            Text(uiState.errorMessage!!)
                        }

                        Button(
                            attrs = Modifier
                                .classNames("button")
                                .backgroundColor(Theme.Primary.rgb)
                                .color(Colors.White)
                                .onClick {
                                    ctx.router.tryRoutingTo(FRONT_ASSET_ROUTE)
                                }
                                .toAttrs()
                        ) {
                            Text("Back to Asset List")
                        }
                    }
                }
            }
            assetDetails != null -> {

                //Button Row
                Row(
                    modifier = Modifier
                        .padding(6.px)
                        .fillMaxWidth()
                        .gap(15.px),
                    horizontalArrangement = Arrangement.Start,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Button(
                        attrs = Modifier
                            .classNames("button")
                            .backgroundColor(Theme.Primary.rgb)
                            .color(Colors.White)
                            .onClick {
                                ctx.router.tryRoutingTo(FRONT_ASSET_ROUTE)
                            }
                            .toAttrs()
                    ) {
                        FaArrowLeft(Modifier.margin(right = 4.px))
                        if (breakpoint > Breakpoint.MD) {
                            Text("Back to Assets")
                        }
                    }

                    Spacer()

                    // ERoad Refresh Buttons
                    Button(
                        attrs = Modifier
                            .classNames("button")
                            .border(1.px, LineStyle.Solid, Theme.Primary.rgb)
                            .backgroundColor(Colors.WhiteSmoke)
                            .color(Theme.Primary.rgb)
                            .onClick {
                                uiState = uiState.copy(
                                    eroadPopup = true,
                                )
                            }
                            .disabled(assetDetails!!.vin.isNullOrBlank() && assetDetails!!.eroadId.isNullOrBlank())
                            .toAttrs()
                    ) {
                        FaRotate(Modifier.margin(right = 4.px).color(Theme.Primary.rgb))
                        Text("ERoad")
                    }

                    Tooltip(
                        target = ElementTarget.PreviousSibling,
                        modifier = Modifier
                            .width(300.px)
                            .padding(6.px)
                            .backgroundColor(Theme.Primary.rgb)
                            .fontSize(0.8.cssRem)
                            .color(Colors.White),
                        showDelayMs = 400
                    ) {
                        Text("System will use VIN and/or ERoad ID to pull latest vehicle miles & update ERoad ID!\n Must match vehicle data on my.eroad.com.")
                    }

                    // Update Button
                    Button(
                        attrs = Modifier
                            .classNames("button", "is-warning")
                            .color(Colors.White)
                            .onClick {
                                uiState = uiState.copy(
                                    updatePopup = true,
                                )
                            }
                            .toAttrs()
                    ) {
                        if (breakpoint < Breakpoint.MD) {
                            FaPencil()
                        } else {
                            Text("Update")
                        }
                    }

                    // Refresh Button
                    Button(
                        attrs = Modifier
                            .classNames("button")
                            .backgroundColor(Theme.Primary.rgb)
                            .color(Colors.White)
                            .onClick {
                                scope.launch {
                                    if (postId != null) {
                                        fetchAssetById(
                                            id = postId,
                                            onSuccess = {
                                                assetDetails = it
                                                uiState = uiState.copy(
                                                    isLoading = false
                                                )
                                            },
                                            onError = {
                                                uiState = uiState.copy(
                                                    errorMessage = it,
                                                    isLoading = false
                                                )
                                            }
                                        )

                                        itemsRowsToSkip = 0 //Reset
                                        fetchAssetMaintenanceItemsByID(
                                            assetID = postId,
                                            status = fetchItemStatus,
                                            skip = itemsRowsToSkip,
                                            onSuccess = {
                                                itemsLoading = false
                                                assetMaintItems.clear() //Reset
                                                assetMaintItems.addAll(it)
                                                itemsRowsToSkip += ROWS_PER_REQ
                                                itemsShowMoreVisibility = it.size >= ROWS_PER_REQ
                                            },
                                            onError = {
                                                console.log(it)
                                            }
                                        )
                                    }
                                }
                            }
                            .toAttrs()
                    ) {
                        if (breakpoint < Breakpoint.MD) {
                            FaRotate()
                        } else {
                            Text("Refresh")
                        }
                    }

                    // Delete Button
                    Button(
                        attrs = Modifier
                            .classNames("button", "is-danger")
                            .color(Colors.White)
                            .onClick {
                                uiState = uiState.copy(
                                    deletePopup = true,
                                )
                            }
                            .toAttrs()
                    ) {
                        if (breakpoint < Breakpoint.MD) {
                            FaTrash()
                        } else {
                            Text("Delete")
                        }
                    }
                }

                assetDetails!!.let { asset ->
                    H2(
                        attrs = AdminTitleHeaderStyle.toAttrs()
                    ) {
                        Text("${asset.id} - ${asset.assetname}")
                    }
                    SpanText(
                        text = asset.equipmentname,
                        modifier = AdminSubtitleHeaderStyle.toModifier()
                    )
                    P(
                        attrs = AdminDescriptionStyle.toAttrs()
                    ) {
                        Text(asset.description)
                    }
                    SpanText(
                        text = "VIN = ${asset.vin ?: "-"}",
                        modifier = AdminSubtitleHeaderStyle
                            .toModifier()
                            .fontSize(1.cssRem)
                    )
                    SpanText(
                        text = "Year = ${asset.year ?: "-"}",
                        modifier = AdminSubtitleHeaderStyle
                            .toModifier()
                            .fontSize(1.cssRem)
                    )
                    SpanText(
                        text = "Created = ${asset.createdAt?.let { formatDate(it) } ?: "NA"}",
                        modifier = AdminSubtitleHeaderStyle
                            .toModifier()
                            .fontSize(1.cssRem)
                    )
                    HorizontalDivider(
                        modifier = Modifier
                            .margin(6.px)
                    )

                    //Stats Row
                    Row(
                        Modifier
                            .fillMaxWidth()
                            .height(100.px)
                            .gap(16.px)
                            .margin(bottom = 32.px),
                        verticalAlignment = Alignment.Top,
                        horizontalArrangement = Arrangement.Start
                    ) {
                        Column(
                            modifier = AdminDetailsColumnStyle.toModifier()
                                .cursor(Cursor.Pointer),
                            horizontalAlignment = Alignment.Start
                        ) {
                            SpanText(
                                text = "Asset Miles:",
                                modifier = AdminTitleHeaderStyle
                                    .toModifier()
                                    .fontSize(1.cssRem)
                            )
                            Row(
                                modifier = Modifier
                                    .onClick {
                                        uiState = uiState.copy(
                                            updatePopup = true,
                                        )
                                    },
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                FaPencil(
                                    Modifier.margin(right = 4.px)
                                )
                                SpanText(
                                    text = asset.miles.pretty(),
                                    modifier = AdminTitleHeaderStyle
                                        .toModifier()
                                )
                            }
//                            Button(
//                                attrs = Modifier
//                                    .classNames("button")
//                                    .border(1.px, LineStyle.Solid, Theme.Primary.rgb)
//                                    .backgroundColor(Colors.WhiteSmoke)
//                                    .color(Theme.Primary.rgb)
//                                    .onClick {
//                                        //TODO endpoint work for refreshing miles per ID
//                                    }
//                                    .toAttrs()
//                            ) {
//                                FaRotate(Modifier.margin(right = 4.px).color(Theme.Primary.rgb))
//                                Text("Pull ERoad Miles")
//                            }
                        }
                        Column(
                            modifier = AdminDetailsColumnStyle.toModifier()
                                .padding(left = 6.px)
                                .borderLeft(2.px, LineStyle.Solid, Colors.LightGray),
                            horizontalAlignment = Alignment.Start
                        ) {
                            SpanText(
                                text = "Status:",
                                modifier = AdminTitleHeaderStyle
                                    .toModifier()
                                    .fontSize(1.cssRem)
                            )
                            asset.status?.let {
                                val parsed = parseAssetStatus(it).joinToString(", ")
                                SpanText(
                                    text = parsed,
                                    modifier = AdminTitleHeaderStyle.toModifier()
                                )
                            }
                        }
                        Column(
                            modifier = AdminDetailsColumnStyle.toModifier()
                                .padding(left = 6.px)
                                .borderLeft(2.px, LineStyle.Solid, Colors.LightGray),
                            horizontalAlignment = Alignment.Start
                        ) {
                            SpanText(
                                text = "Type:",
                                modifier = AdminTitleHeaderStyle
                                    .toModifier()
                                    .fontSize(1.cssRem)
                            )
                            asset.type?.let {
                                val parsed = parseAssetTypes(it).joinToString(", ")
                                SpanText(
                                    text = parsed,
                                    modifier = AdminTitleHeaderStyle.toModifier()
                                )
                            }
                        }
                        Column(
                            modifier = AdminDetailsColumnStyle.toModifier()
                                .width(350.px)
                                .borderLeft(2.px, LineStyle.Solid, Colors.LightGray)
                                .padding(left = 6.px),
                            horizontalAlignment = Alignment.Start
                        ) {
                            SpanText(
                                text = "ERoad ID:",
                                modifier = AdminTitleHeaderStyle
                                    .toModifier()
                                    .fontSize(1.cssRem)
                            )
                            Row(
                                modifier = Modifier
                                    .margin(bottom = 8.px)
                                    .cursor(Cursor.Pointer)
                                    .onClick {
                                        uiState = uiState.copy(
                                            updatePopup = true,
                                        )
                                    },
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                FaPencil(
                                    Modifier.margin(right = 4.px)
                                )
                                SpanText(
                                    text = asset.eroadId ?: "Input valid ERoad ID or VIN!",
                                    modifier = AdminTitleHeaderStyle.toModifier()
                                        .fontSize(1.cssRem)
                                )
                                Tooltip(
                                    target = ElementTarget.PreviousSibling,
                                    modifier = Modifier
                                        .padding(4.px)
                                        .backgroundColor(Theme.Primary.rgb)
                                        .color(Colors.White),
                                ) {
                                    Text("System will use VIN and/or ERoad ID to pull latest vehicle data!\n Must match vehicle data on my.eroad.com.")
                                }
                            }
//                            Button(
//                                attrs = Modifier
//                                    .classNames("button")
//                                    .border(1.px, LineStyle.Solid, Theme.Primary.rgb)
//                                    .backgroundColor(Colors.WhiteSmoke)
//                                    .color(Theme.Primary.rgb)
//                                    .onClick {
//                                        //TODO endpoint work for refreshing miles per ID
//                                    }
//                                    .toAttrs()
//                            ) {
//                                FaRotate(Modifier.margin(right = 4.px).color(Theme.Primary.rgb))
//                                Text("Refresh ERoad ID")
//                            }
                        }
//                        Column(
//                            modifier = AdminDetailsColumnStyle.toModifier()
//                                .padding(left = 6.px)
//                                .borderLeft(2.px, LineStyle.Solid, Colors.LightGray),
//                            horizontalAlignment = Alignment.Start
//                        ) {
//                            SpanText(
//                                text = "Schedules:",
//                                modifier = AdminTitleHeaderStyle
//                                    .toModifier()
//                                    .fontSize(1.cssRem)
//                            )
//
//                            Ul(
//                                attrs = Modifier
//                                    .fillMaxHeight()
//                                    .overflow(Overflow.Auto)
//                                    .margin(0.px)
//                                    .toAttrs()
//                            ) {
//                                assetMaintItems.forEach { maintItem ->
//                                    Li(
//                                        attrs = Modifier
//                                            .margin(0.px)
//                                            .padding(0.px)
//                                            .toAttrs()
//                                    ) {
//                                        SpanText(
//                                            text = maintItem.scheduleName!!,
//                                            modifier = AdminSubtitleHeaderStyle
//                                                .toModifier()
//                                                .fontSize(0.95.cssRem)
//                                        )
//                                    }
//                                }
//                            }
//                        }
                    }

                    // Buttons Above Maintenance Item Details
                    Row(
                        Modifier
                            .fillMaxWidth()
                            .gap(16.px)
                            .margin(bottom = 8.px),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.Start
                    ) {
                        // Add Maintenance Schedule Button
                        Button(
                            attrs = Modifier
                                .classNames("button")
                                .backgroundColor(Theme.Primary.rgb)
                                .color(Colors.White)
                                .onClick {
                                    uiState = uiState.copy(
                                        schedulePopup = true
                                    )
                                }
                                .toAttrs()
                        ) {
                            FaPlus(Modifier.margin(right = 4.px))
                            Text("Add Maintenance Schedule")
                        }

                        // Dropdown for Maintenance Item Status
                        Select(
                            attrs = Modifier
                                .width(265.px)
                                .height(40.px)
                                .margin(topBottom = 12.px)
                                .padding(leftRight = 20.px)
                                .backgroundColor(Theme.LightGray.rgb)
                                .borderRadius(r = 4.px)
                                .border(
                                    width = 1.px,
                                    style = LineStyle.Solid,
                                    color = Colors.Black
                                )
                                .fontFamily(FONT_FAMILY)
                                .fontSize(16.px)
                                .id(InputIDs.MaintenanceItems.MaintItemOnAssetStatus)
                                .toAttrs{
                                    onChange {
                                        val selectedStatus = (document.getElementById(InputIDs.MaintenanceItems.MaintItemOnAssetStatus) as HTMLSelectElement).value
                                        fetchItemStatus = selectedStatus.toShortOrNull()
                                    }
                                }
                        ) {
                            Option(
                                value = "",
                                attrs = Modifier
                                    .toAttrs{
                                        if (fetchItemStatus == null) {
                                            selected()
                                        }
                                    }
                            ) {
                                Text("Default")
                            }
                            MaintItemStatus.asPairList().forEach { status ->
                                Option(
                                    value = status.first.toString(),
                                    attrs = Modifier
                                        .toAttrs{
                                            if (status.first == fetchItemStatus) {
                                                selected()
                                            }
                                        }
                                ) {
                                    Text(status.second)
                                }
                            }
                        }

                        Spacer()

                        SpanText(
                            text = "${assetMaintItems.size} Total Item(s)",
                            modifier = Modifier
                                .fontSize(18.px)
                                .fontFamily(FONT_FAMILY2)
                                .fontWeight(FontWeight.Light)
                        )
                    }

                    AssetMaintenanceItemsTable(
                        assetMaintItems = assetMaintItems,
                        onComplete = { item ->
                            uiState = uiState.copy(
                                markCompletePopup = true,
                                selectedMaintItem = item
                            )
                        },
                        onRead = { item ->
                            uiState = uiState.copy(
                                readMaintPopup = true,
                                selectedMaintItem = item
                            )
                        },
                        onDelete = { item ->
                            uiState = uiState.copy(
                                deleteMaintPopup = true,
                                selectedMaintItem = item
                            )
                        },
                        showMoreVisibility = itemsShowMoreVisibility,
                        onShowMore = {
                            scope.launch {
                                if (postId != null) {
                                    itemsLoading = true
                                    fetchAssetMaintenanceItemsByID(
                                        assetID = postId,
                                        status = fetchItemStatus,
                                        skip = itemsRowsToSkip,
                                        onSuccess = {
                                            itemsLoading = false
                                            assetMaintItems.addAll(it)
                                            itemsRowsToSkip += ROWS_PER_REQ
                                            itemsShowMoreVisibility = it.size >= ROWS_PER_REQ
                                            if (it.size < ROWS_PER_REQ) {
                                                itemsShowMoreVisibility = false
                                            }
                                        },
                                        onError = {
                                            console.log(it)
                                        }
                                    )
                                }
                            }
                        }
                    )
                }
            }
        }
    }
}